import React, { useEffect, useState } from "react";
import { Mail, Building2, Truck, Trash2, Globe2, Phone, Link2 } from "lucide-react";
import { toast } from "../../components/ui/sonner";
import { api, errorMessage } from "../../api";

const STATUSES = ["new", "contacted", "qualified", "closed"];
const TONE = {
  new: "!bg-[var(--fr-primary-tint)] !text-[var(--fr-primary-dark)] !border-transparent",
  contacted: "!bg-amber-50 !text-amber-700 !border-amber-100",
  qualified: "!bg-emerald-50 !text-emerald-700 !border-emerald-100",
  closed: "!bg-[var(--fr-bg-alt)] !text-[var(--fr-muted)]",
};

const fmt = (iso) => new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

const SOURCES = {
  demo: { path: "/demo-requests", label: "Demo requests", eyebrow: "Inbox", blurb: "Everything submitted through the Contact page. Update the status as you follow up." },
  partner: { path: "/partner-applications", label: "Partner applications", eyebrow: "Partner program", blurb: "Companies applying to resell or implement Fleetroot in their country." },
};

export default function AdminLeads() {
  const [source, setSource] = useState("demo");
  const [leads, setLeads] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const cfg = SOURCES[source];

  const load = React.useCallback(() => {
    setLeads(null);
    setError(null);
    api
      .get(cfg.path, { timeout: 20000 })
      .then((r) => setLeads(r.data))
      .catch((e) => setError(e.code === "ECONNABORTED" ? "The server took too long to respond." : errorMessage(e)));
  }, [cfg.path]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (lead, status) => {
    try {
      const { data } = await api.patch(`${cfg.path}/${lead.id}`, { status });
      setLeads((l) => l.map((x) => (x.id === lead.id ? data : x)));
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const remove = async (lead) => {
    if (!window.confirm(`Delete lead from ${lead.name}?`)) return;
    try {
      await api.delete(`${cfg.path}/${lead.id}`);
      setLeads((l) => l.filter((x) => x.id !== lead.id));
      toast.success("Lead deleted");
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const shown = (leads || []).filter((l) => filter === "all" || l.status === filter);

  return (
    <div data-testid="admin-leads-page">
      <span className="eyebrow">{cfg.eyebrow}</span>
      <h1 className="font-display font-bold text-3xl md:text-4xl mt-2">{cfg.label}</h1>
      <p className="text-sm text-[var(--fr-muted)] mt-2">{cfg.blurb}</p>
      <div className="mt-6 inline-flex gap-1 bg-[var(--fr-bg-alt)] p-1 rounded-xl" data-testid="admin-leads-source">
        {Object.entries(SOURCES).map(([k, v]) => (
          <button key={k} type="button" onClick={() => { setSource(k); setFilter("all"); }} className={`h-9 px-4 rounded-lg text-sm font-medium ${source === k ? "bg-white shadow-sm text-[var(--fr-ink)]" : "text-[var(--fr-muted)]"}`} data-testid={`admin-leads-source-${k}`}>
            {v.label}
          </button>
        ))}
      </div>

      <div className="mt-5 flex gap-2 flex-wrap" data-testid="admin-leads-filters">
        {["all", ...STATUSES].map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`h-9 px-4 rounded-full text-sm font-medium border capitalize transition-colors ${
              filter === f ? "bg-[var(--fr-ink)] text-white border-[var(--fr-ink)]" : "bg-white text-[var(--fr-ink-2)] border-[var(--fr-border)] hover:border-[var(--fr-ink)]"
            }`}
            data-testid={`admin-leads-filter-${f}`}
          >
            {f} <span className="opacity-60">{f === "all" ? leads?.length || 0 : leads?.filter((l) => l.status === f).length || 0}</span>
          </button>
        ))}
      </div>

      <div className="card mt-6 overflow-hidden">
        {error ? (
          <div className="p-10 text-center" data-testid="admin-leads-error">
            <h3 className="font-display font-bold text-lg">Couldn't load leads</h3>
            <p className="text-sm text-[var(--fr-muted)] mt-1">{error}</p>
            <button type="button" onClick={load} className="btn btn-outline mt-4" data-testid="admin-leads-retry">
              Try again
            </button>
          </div>
        ) : leads === null ? (
          <div className="p-10 text-center text-sm text-[var(--fr-muted)]">Loading…</div>
        ) : shown.length === 0 ? (
          <div className="p-12 text-center" data-testid="admin-leads-empty">
            <h3 className="font-display font-bold text-lg">No leads in this view</h3>
            <p className="text-sm text-[var(--fr-muted)] mt-1">New submissions will appear here.</p>
          </div>
        ) : (
          <ul className="divide-y divide-[var(--fr-border)]" data-testid="admin-leads-list">
            {shown.map((l) => (
              <li key={l.id} className="p-5 md:px-6 grid md:grid-cols-[1fr_auto] gap-4" data-testid={`admin-lead-${l.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-display font-bold" data-testid="lead-name">{l.name}</span>
                    <span className={`chip !text-[11px] uppercase tracking-wide ${TONE[l.status]}`} data-testid="lead-status">
                      {l.status}
                    </span>
                    <span className="text-xs text-[var(--fr-muted)]">{fmt(l.createdAt)}</span>
                    {l.source === "assistant" && <span className="chip !text-[10px] uppercase tracking-wide" data-testid="lead-source-assistant">AI chat</span>}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-[var(--fr-ink-2)]">
                    <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1.5 hover:text-[var(--fr-primary-dark)]" data-testid="lead-email">
                      <Mail size={13} /> {l.email}
                    </a>
                    <span className="inline-flex items-center gap-1.5">
                      <Building2 size={13} /> {l.company}
                    </span>
                    {l.fleetSize && (
                      <span className="inline-flex items-center gap-1.5">
                        <Truck size={13} /> {l.fleetSize}
                      </span>
                    )}
                    {l.country && (
                      <span className="inline-flex items-center gap-1.5">
                        <Globe2 size={13} /> {l.country} · {l.partnerType}
                      </span>
                    )}
                    {l.phone && (
                      <span className="inline-flex items-center gap-1.5">
                        <Phone size={13} /> {l.phone}
                      </span>
                    )}
                    {l.website && (
                      <a href={l.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-[var(--fr-primary-dark)]">
                        <Link2 size={13} /> {l.website.replace(/^https?:\/\//, "")}
                      </a>
                    )}
                  </div>
                  {l.message && <p className="mt-3 text-sm text-[var(--fr-ink-2)] bg-[var(--fr-bg)] border border-[var(--fr-border)] rounded-xl px-4 py-3 whitespace-pre-wrap">{l.message}</p>}
                </div>
                <div className="flex md:flex-col items-center md:items-end gap-2">
                  <select className="fr-input !h-9 !w-auto text-sm capitalize" value={l.status} onChange={(e) => setStatus(l, e.target.value)} data-testid="lead-status-select">
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                  <button type="button" onClick={() => remove(l)} className="icon-btn danger" title="Delete" data-testid="lead-delete">
                    <Trash2 size={14} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
